/**
 * Customer Growth Chart Component
 * Displays cumulative customer growth over time with dynamic updates
 * Fully responsive, accessible, and user-friendly
 */

import React, { useState, useMemo } from 'react';
import { CustomerGrowthData } from '../../types';
import { TrendingUp } from 'lucide-react';

interface CustomerGrowthChartProps {
  data: CustomerGrowthData[];
  isLoading?: boolean;
  error?: string | null;
}

// Constants for chart rendering
const CHART_CONFIG = {
  MIN_HEIGHT: 200,
  CHART_HEIGHT: 160,
  PADDING: 16,
  BASELINE_Y: 140,
  MAX_SCALE: 130,
  DOT_RADIUS: 4,
  STROKE_WIDTH: 2.5,
  GRID_COLOR: '#e5e7eb',
  LINE_COLOR: '#3b82f6',
  DOT_COLOR: '#3b82f6',
  HOVER_COLOR: '#1e40af'
};

export const CustomerGrowthChart: React.FC<CustomerGrowthChartProps> = ({
  data,
  isLoading = false,
  error = null
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // Calculate chart metrics
  const chartMetrics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        maxCustomers: 0,
        minCustomers: 0,
        totalNewCustomers: 0,
        hasData: false,
        dataPoints: 0,
        growth: 0
      };
    }

    const customers = data.map(d => d.totalCustomers);
    const maxCustomers = Math.max(...customers, 0);
    const minCustomers = Math.min(...customers, 0);
    const totalNewCustomers = data.reduce((sum, d) => sum + d.newCustomers, 0);
    const firstTotal = data[0]?.totalCustomers || 0;
    const lastTotal = data[data.length - 1]?.totalCustomers || 0;
    const growth = lastTotal - firstTotal;

    return {
      maxCustomers: Math.max(maxCustomers, 10), // Ensure minimum scale
      minCustomers,
      totalNewCustomers,
      hasData: maxCustomers > 0 || totalNewCustomers > 0,
      dataPoints: data.length,
      growth,
      growthPercentage: firstTotal > 0 ? ((growth / firstTotal) * 100).toFixed(1) : 0
    };
  }, [data]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600"></div>
        <p className="text-sm text-gray-500">Loading customer data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="text-sm text-red-700">{error}</div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0 || !chartMetrics.hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3 text-gray-400">
        <TrendingUp className="w-12 h-12 opacity-30" />
        <div className="text-center">
          <p className="font-medium text-gray-600">No customer data available</p>
          <p className="text-sm text-gray-500 mt-1">No new customers registered in selected period</p>
        </div>
      </div>
    );
  }

  // Calculate SVG dimensions
  const svgWidth = Math.max(400, chartMetrics.dataPoints * 50);
  const spacePerPoint = svgWidth / (chartMetrics.dataPoints || 1);

  // Handle tooltip position
  const handleDotHover = (index: number, event: React.MouseEvent<SVGCircleElement>) => {
    setHoveredIndex(index);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPos({
      x: rect.left,
      y: rect.top - 10
    });
  };

  return (
    <div className="space-y-4 w-full">
      {/* Chart Container */}
      <div className="relative w-full bg-gradient-to-br from-blue-50 to-gray-50 p-4 rounded-lg border border-gray-200 overflow-x-auto">
        <div style={{ minHeight: `${CHART_CONFIG.MIN_HEIGHT}px` }}>
          <svg
            className="w-full h-auto"
            viewBox={`0 0 ${svgWidth} 180`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="Customer growth chart showing cumulative customer registrations over time"
          >
            {/* Y-axis labels and grid */}
            <g className="text-xs fill-gray-500">
              <line x1="0" y1="10" x2={svgWidth} y2="10" stroke={CHART_CONFIG.GRID_COLOR} strokeWidth="0.5" opacity="0.5" />
              <line x1="0" y1="75" x2={svgWidth} y2="75" stroke={CHART_CONFIG.GRID_COLOR} strokeWidth="0.5" opacity="0.5" />
              <line x1="0" y1="140" x2={svgWidth} y2="140" stroke={CHART_CONFIG.GRID_COLOR} strokeWidth="1" />

              {/* Y-axis values */}
              <text x="-5" y="145" textAnchor="end" fontSize="10" fill="#9ca3af">0</text>
              <text x="-5" y="80" textAnchor="end" fontSize="10" fill="#9ca3af">{Math.round(chartMetrics.maxCustomers / 2)}</text>
              <text x="-5" y="15" textAnchor="end" fontSize="10" fill="#9ca3af">{chartMetrics.maxCustomers}</text>
            </g>

            {/* Line path (gradient effect) */}
            {chartMetrics.dataPoints > 1 && (
              <>
                {/* Gradient definition */}
                <defs>
                  <linearGradient id="growthGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: CHART_CONFIG.LINE_COLOR, stopOpacity: 0.3 }} />
                    <stop offset="100%" style={{ stopColor: CHART_CONFIG.LINE_COLOR, stopOpacity: 0 }} />
                  </linearGradient>
                </defs>

                {/* Fill area under the line */}
                <polyline
                  points={data
                    .map((item, idx) => {
                      const x = idx * spacePerPoint + 20;
                      const y = CHART_CONFIG.BASELINE_Y - (item.totalCustomers / chartMetrics.maxCustomers) * CHART_CONFIG.MAX_SCALE;
                      return `${x},${y}`;
                    })
                    .concat([
                      `${(data.length - 1) * spacePerPoint + 20},${CHART_CONFIG.BASELINE_Y}`,
                      `20,${CHART_CONFIG.BASELINE_Y}`
                    ])
                    .join(' ')}
                  fill="url(#growthGradient)"
                  opacity="0.5"
                />

                {/* Main line */}
                <polyline
                  points={data
                    .map((item, idx) => {
                      const x = idx * spacePerPoint + 20;
                      const y = CHART_CONFIG.BASELINE_Y - (item.totalCustomers / chartMetrics.maxCustomers) * CHART_CONFIG.MAX_SCALE;
                      return `${x},${y}`;
                    })
                    .join(' ')}
                  fill="none"
                  stroke={CHART_CONFIG.LINE_COLOR}
                  strokeWidth={CHART_CONFIG.STROKE_WIDTH}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            )}

            {/* Data points */}
            {data.map((item, idx) => {
              const x = idx * spacePerPoint + 20;
              const y = CHART_CONFIG.BASELINE_Y - (item.totalCustomers / chartMetrics.maxCustomers) * CHART_CONFIG.MAX_SCALE;
              const isHovered = hoveredIndex === idx;

              return (
                <g key={`point-${idx}`}>
                  {/* Hover background circle */}
                  {isHovered && (
                    <circle
                      cx={x}
                      cy={y}
                      r={CHART_CONFIG.DOT_RADIUS + 4}
                      fill={CHART_CONFIG.HOVER_COLOR}
                      opacity="0.1"
                    />
                  )}

                  {/* Data point circle */}
                  <circle
                    cx={x}
                    cy={y}
                    r={isHovered ? CHART_CONFIG.DOT_RADIUS + 2 : CHART_CONFIG.DOT_RADIUS}
                    fill={isHovered ? CHART_CONFIG.HOVER_COLOR : CHART_CONFIG.DOT_COLOR}
                    stroke="white"
                    strokeWidth={isHovered ? 2 : 1}
                    style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
                    onMouseEnter={(e) => handleDotHover(idx, e)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    role="button"
                    tabIndex={0}
                    aria-label={`${item.date}: ${item.totalCustomers} total customers, +${item.newCustomers} new`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleDotHover(idx, e as unknown as React.MouseEvent<SVGCircleElement>);
                      }
                    }}
                  />
                </g>
              );
            })}

            {/* X-axis */}
            <line x1="20" y1={CHART_CONFIG.BASELINE_Y} x2={svgWidth - 10} y2={CHART_CONFIG.BASELINE_Y} stroke={CHART_CONFIG.GRID_COLOR} strokeWidth="2" />
          </svg>
        </div>

        {/* Tooltip */}
        {hoveredIndex !== null && data[hoveredIndex] && (
          <div className="fixed bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none z-50 whitespace-nowrap animate-fade-in"
            style={{
              left: `${tooltipPos.x}px`,
              top: `${tooltipPos.y}px`,
              transform: 'translate(-50%, -100%)'
            }}
          >
            <div className="font-semibold">{data[hoveredIndex].date}</div>
            <div>Total: <span className="font-bold">{data[hoveredIndex].totalCustomers}</span></div>
            <div className="text-green-300">New: +{data[hoveredIndex].newCustomers}</div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
          </div>
        )}

        {/* X-axis labels */}
        <div className="flex justify-between text-xs text-gray-500 px-4 mt-2">
          <span>{data[0]?.date}</span>
          <span>{data[Math.floor(data.length / 2)]?.date}</span>
          <span>{data[data.length - 1]?.date}</span>
        </div>
      </div>

      {/* Legend and Metrics */}
      <div className="space-y-3">
        {/* Legend */}
        <div className="flex items-center gap-2 px-1">
          <div className="w-4 h-4 bg-blue-500 rounded-full shadow-md"></div>
          <span className="text-xs font-medium text-gray-700">Cumulative Customer Growth</span>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Current Total */}
          <div className="p-3 bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg border border-blue-100">
            <div className="text-xs text-gray-600 font-medium">Current Total</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">
              {data[data.length - 1]?.totalCustomers || 0}
            </div>
            <div className="text-xs text-gray-500 mt-1">Active customers</div>
          </div>

          {/* New This Period */}
          <div className="p-3 bg-gradient-to-br from-green-50 to-gray-50 rounded-lg border border-green-100">
            <div className="text-xs text-gray-600 font-medium">New This Period</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              +{chartMetrics.totalNewCustomers}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {chartMetrics.growthPercentage > 0 ? `${chartMetrics.growthPercentage}% growth` : 'No new customers'}
            </div>
          </div>
        </div>

        {/* Additional Stats */}
        {chartMetrics.dataPoints > 1 && (
          <div className="p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-600">
            <span className="font-medium">Period Analysis:</span> {chartMetrics.dataPoints} days tracked • Starting: {data[0]?.totalCustomers || 0} • Ending: {data[data.length - 1]?.totalCustomers || 0}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerGrowthChart;
